import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/firestore';
import { OrderTemplateInterface, OrderTemplateInterfaceWithId, ProjectInterfaceWithId } from '@sk/sk-interfaces';
import { Observable } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { LocalStorageService } from 'services/local-storage.service';
import { fromPromise } from 'rxjs/internal-compatibility';
import * as moment from 'moment';
import firebase from 'firebase/app';

@Injectable({
  providedIn: 'root'
})
export class OrderTemplateService {

  constructor(private firestoreService: AngularFirestore,
              private storageService: LocalStorageService) {
  }

  // NGRX RELATED FUNCTIONS
  queryStateChangesTemplates() {
    console.log(`RUN TEMPLATES QUERY - ${moment().format('HH:mm:ss')}`);
    const organizationId = this.storageService.getStorage('organizationId');
    return this.firestoreService.collection('organizations').doc(organizationId)
      .collection<OrderTemplateInterface>('order-templates')
      .stateChanges()
      .pipe(
        finalize(() => console.log('TEMPLATES QUERY DONE'))
      );
  }


  addTemplateByNgrx(templateData: OrderTemplateInterfaceWithId): Observable<OrderTemplateInterfaceWithId> {
    const organizationId = this.storageService.getOrganizationId();
    const newTemplateObj = {
      ...templateData
    };
    if (newTemplateObj.id) {
      delete newTemplateObj.id;
    }
    if (newTemplateObj.exists) {
      delete newTemplateObj.exists;
    }
    return fromPromise(
      this.firestoreService
        .collection('organizations').doc(organizationId)
        .collection('order-templates').add(newTemplateObj))
      .pipe(
        map(res => {
          const exists = true;
          const id = res.id;
          return { ...templateData, id, exists } as OrderTemplateInterfaceWithId;
        }));
  }


  updateTemplateByIdWithNGRX(templateData: OrderTemplateInterfaceWithId, id: string): Observable<OrderTemplateInterfaceWithId> {
    const organizationId = this.storageService.getOrganizationId();
    const updateTemplateObj = {
      ...templateData
    };
    if (updateTemplateObj.id) {
      delete updateTemplateObj.id;
    }
    if (updateTemplateObj.exists) {
      delete updateTemplateObj.exists;
    }
    return fromPromise(
      this.firestoreService
        .collection('organizations').doc(organizationId)
        .collection('order-templates').doc(id).update(updateTemplateObj))
      .pipe(
        map(() => {
          const exists = true;
          const template = { ...templateData, id, exists } as OrderTemplateInterfaceWithId;
          return template;
        }));
  }


  // Only give me the orderTemplates with a costModel and that are active
  getOrderTemplatesCostModel(): Observable<OrderTemplateInterfaceWithId[]> {
    const organizationId = this.storageService.getStorage('organizationId');
    return this.firestoreService.collection('organizations').doc(organizationId)
      .collection('order-templates', ref => {
        let query: firebase.firestore.CollectionReference | firebase.firestore.Query = ref;
        query = query.orderBy('costModel', 'asc');
        query = query.where('active', '==', true);
        return query;
      })
      .snapshotChanges()
      .pipe(
        map(actions => actions.map(a => {
          const data = a.payload.doc.data() as OrderTemplateInterface;
          const id = a.payload.doc.id;
          const exists = a.payload.doc.exists;
          return { ...data, id, exists } as OrderTemplateInterfaceWithId;
        }))
      );
  }


  getOrderTemplate(id): Observable<OrderTemplateInterfaceWithId> {
    const organizationId = this.storageService.getStorage('organizationId');
    return this.firestoreService.collection('organizations').doc(organizationId)
      .collection('order-templates').doc(id).get()
      .pipe(
        map(res => {
          const orderTemplate = res.data() as OrderTemplateInterface;
          const exists = res.exists;
          const newOrderTemplate = { ...orderTemplate, id, exists } as OrderTemplateInterfaceWithId;
          return newOrderTemplate;
        }));
  }




  getOrderTemplatesProject(projectId: string): Observable<OrderTemplateInterfaceWithId[]> {
    const organizationId = this.storageService.getStorage('organizationId');
    return this.firestoreService.collection('organizations').doc(organizationId)
      .collection('order-templates', ref =>
        ref.where('projectId', '==', projectId)).snapshotChanges()
      .pipe(
        map(actions => actions.map(a => {
          const data = a.payload.doc.data() as OrderTemplateInterface;
          const id = a.payload.doc.id;
          const exists = a.payload.doc.exists;
          return { ...data, id, exists } as OrderTemplateInterfaceWithId;
        }))
      );
  }

  getOrderTemplatesTechnician(technicianId: string): Observable<OrderTemplateInterfaceWithId[]> {
    const organizationId = this.storageService.getStorage('organizationId');
    return this.firestoreService.collection('organizations').doc(organizationId)
      .collection('order-templates', ref =>
        ref.where('technicianId', '==', technicianId)).snapshotChanges()
      .pipe(
        map(actions => actions.map(a => {
          const data = a.payload.doc.data() as OrderTemplateInterface;
          const id = a.payload.doc.id;
          const exists = a.payload.doc.exists;
          return { ...data, id, exists } as OrderTemplateInterfaceWithId;
        }))
      );
  }

  addOrderTemplate(orderTemplate) {
    const organizationId = this.storageService.getStorage('organizationId');
    return fromPromise(this.firestoreService
      .collection('organizations').doc(organizationId)
      .collection('order-templates').add(orderTemplate))
      .pipe(
        map(res => {
          const id = res.id;
          const exists = true;
          const newOrderTemplate = {...orderTemplate, id, exists } as OrderTemplateInterfaceWithId;
          return newOrderTemplate;
        }));
  }

  deleteOrderTemplate(orderTemplateId: string) {
    const organizationId = this.storageService.getStorage('organizationId');
    return fromPromise(this.firestoreService
      .collection('organizations').doc(organizationId)
      .collection('order-templates').doc(orderTemplateId).delete());
  }

  updateOrderTemplate(orderTemplate, orderTemplateId: string) {
    const organizationId = this.storageService.getStorage('organizationId');
    const template = {...orderTemplate};
    delete template.id;
    delete template.exists;
    return fromPromise(this.firestoreService
      .collection('organizations').doc(organizationId)
      .collection('order-templates').doc(orderTemplateId).update(template));
  }


}
